'use client';

import { useState, type JSX } from 'react';

import styles from './CommercialSecurityPage.module.css';
import type { CommercialSecurityAccordionItem, CommercialSecurityRichSegment } from './types';

function RichText({ segments }: { segments: CommercialSecurityRichSegment[] }): JSX.Element {
  return (
    <>
      {segments.map((segment, index) => {
        const key = `${segment.kind}-${index}`;
        if (segment.kind === 'break') return <br key={key} />;
        if (segment.kind === 'strong') return <strong key={key}>{segment.text}</strong>;
        if (segment.kind === 'em') return <em key={key}>{segment.text}</em>;
        if (segment.kind === 'link') {
          const rel =
            segment.rel ?? (segment.target === '_blank' ? 'noopener noreferrer' : undefined);
          return (
            <a
              key={key}
              href={segment.href}
              target={segment.target}
              rel={rel}
              className={styles.link}
            >
              {segment.strong ? <strong>{segment.text}</strong> : segment.text}
            </a>
          );
        }
        return <span key={key}>{segment.text}</span>;
      })}
    </>
  );
}

export function CommercialSecurityAccordion({
  blockId,
  items,
}: {
  blockId: string;
  items: CommercialSecurityAccordionItem[];
}): JSX.Element {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());

  const toggle = (itemId: string) => {
    setOpenIds((previous) => {
      const next = new Set(previous);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  return (
    <div className={styles.accordionWrapper} data-cms-block-id={blockId}>
      {items.map((item) => {
        const open = openIds.has(item.blockId);
        const buttonId = `${item.blockId}-button`;
        const panelId = `${item.blockId}-panel`;
        return (
          <div key={item.blockId} className={styles.accordionItem} data-cms-block-id={item.blockId}>
            <button
              id={buttonId}
              type="button"
              className={`${styles.accordionButton}${open ? ` ${styles.accordionButtonOpen}` : ''}`}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggle(item.blockId)}
              data-no-cms-button
            >
              {item.label}
            </button>
            <div
              id={panelId}
              className={styles.accordionBody}
              role="region"
              aria-labelledby={buttonId}
              hidden={!open}
            >
              {item.paragraphs.map((paragraph, index) => (
                <p key={`${item.blockId}-paragraph-${index}`} className={styles.paragraph}>
                  <RichText segments={paragraph} />
                </p>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
